import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { fetchInvites, respondToInvite, fetchInvite, fetchPendingInvitesByOrganization, fetchPendingInvitesByLicense } from '../actions/AppActions';
import { getInvitations, getCurrentUserId, getPendingResponses, getInvitation, getUserEmail } from '../selectors/PersonProfileSelectors';
import { getSelectedProfileOrg, getSelectedOrganizationInvitations } from '../selectors/OrgManagementSelectors';
import { getLicenseSeatInvitations } from '../selectors/LicenseSelectors';
import { toast } from 'react-toastify';
import { shape, string } from 'prop-types';
import { injectParams } from '../utils/pathHelpers';
import { INV_PATHS } from '../constants';
import history from '../utils/history';
import httpClient from '../utils/httpClient';
import { poll } from '../utils/poll';

export const usePendingInvitations = () => {
  const dispatch = useDispatch();
  const { loading, error, data } = useSelector(getInvitations);
  const currentUserId = useSelector(getCurrentUserId);
  useEffect(() => {
    if (currentUserId) {
      dispatch(fetchInvites(currentUserId));
    }
  }, [currentUserId]);

  return { loading, error, invitations: data };
};

export const useInvitation = (inviteId) => {
  const dispatch = useDispatch();
  const { loading, error, data } = useSelector(getInvitation);
  useEffect(() => {
    if (inviteId) {
      dispatch(fetchInvite(inviteId));
    }
  }, [inviteId]);
  return { loading, error, invitation: data };
};

const checkAck = (inviteId) => () =>
  httpClient()
    .get(`/invitations/${inviteId}`)
    .then(resp => !!(resp.data.acknowledged));

export const useAcknowledgementPolling = (inviteId) => {
  const [polling, setPolling] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [pollingError, setPollingError] = useState(undefined);
  const startPolling = useCallback(() => setPolling(true), [setPolling]);
  const stopPolling = useCallback(() => setPolling(false), [setPolling]);

  useEffect(() => {
    if (polling) {
      const { promise, halt } = poll(
        checkAck(inviteId),
        acknowledged => acknowledged
      );

      promise
        .then(setAcknowledged)
        .catch(setPollingError)
        .finally(() => setPolling(false));

      return halt;
    }
  }, [polling, inviteId]);

  return { startPolling, stopPolling, polling, acknowledged, pollingError };
};

export const usePendingInvitation = (invitationId) => {
  const { loading, error, invitations } = usePendingInvitations();
  const invitation = useMemo(() => {
    return invitations.filter(isValidInvitation).find(inv => inv.inviteId === invitationId);
  }, [invitations, invitationId]);
  return { loading, error, invitation };
};

export const isValidGroupInvitation = (invitation) =>
  !!(
    invitation?.meta?.organizationName &&
    invitation?.meta?.organizationId &&
    invitation?.meta?.groupName &&
    invitation?.meta?.groupId &&
    invitation?.meta?.adminName
  );

export const isValidLicenseInvitation = (invitation) =>
  !!(
    invitation?.meta?.licenseId &&
    invitation?.meta?.managerName &&
    invitation?.meta?.managerId &&
    invitation?.meta?.productNumber &&
    invitation?.meta?.productTitle &&
    (invitation?.meta?.seat || invitation?.meta?.manager)
  );
export const isValidInvitation = (invitation) => isValidGroupInvitation(invitation) || isValidLicenseInvitation(invitation);

const isAlreadyDismissed = (inv) => {
  return !localStorage.getItem(`invite.${inv.inviteId}`);
};

const InvitationToast = ({ inviteMeta }) => {
  return (
      <>You&apos;ve been invited to join the {inviteMeta.groupName} group for {inviteMeta.organizationName}!</>
  );
};
InvitationToast.propTypes = {
  inviteMeta: shape({
    groupName: string.isRequired,
    organizationName: string.isRequired,
    adminName: string.isRequired,
  })
};

export const useInvitationNotifications = (invitations) => {
  const dismissInvite = (invId) => () => localStorage.setItem(`invite.${invId}`, true);
  const goToInvite = (inviteId) => () => history.push(injectParams(INV_PATHS.ORG_INVITE, { inviteId }));
  useEffect(() => {
    invitations
      .filter(isValidInvitation)
      .filter(isAlreadyDismissed)
      .map(inv => {
        toast.info(<InvitationToast inviteMeta={inv.meta} />, {
          position: 'top-right',
          autoClose: 5000,
          delay: 500,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          onClose: dismissInvite(inv.inviteId),
          onClick: goToInvite(inv.inviteId)
        });
      });
  }, [invitations]);
};

export const usePendingResponses = () => {
  return useSelector(getPendingResponses);
};

export const useRespondInvitation = (inviteId, afterAccept, afterDecline) => {
  const dispatch = useDispatch();
  const email = useSelector(getUserEmail);
  const accept = useCallback(() => dispatch(
    respondToInvite({ inviteId, response: 'accept', email }, afterAccept)),
    [inviteId, email, afterAccept]
  );
  const decline = useCallback(() => dispatch(
    respondToInvite({ inviteId, response: 'decline' }, afterDecline)),
    [inviteId, afterDecline]
  );
  const allResponseErrors = useSelector(state => state.invitations.get('responseErrors'));
  const responseError = useMemo(() => {
    return allResponseErrors.find(e => e.inviteId === inviteId);
  }, [allResponseErrors, inviteId]);
  return {
    responseError,
    accept,
    decline
  };
};

export const useGroupInvitations = (groupId) => {
  const dispatch = useDispatch();
  const organization = useSelector(getSelectedProfileOrg);
  const { loading, error, data } = useSelector(getSelectedOrganizationInvitations);
  useEffect(() => {
    if (organization) {
      dispatch(fetchPendingInvitesByOrganization(organization.organizationId));
    }
  }, [organization]);
  return {
    loading,
    error,
    invitations: data?.filter(invitation => invitation.meta?.groupId === groupId),
  };
};

export const useLicenseInvitations = (licenseId) => {
  const dispatch = useDispatch();
  const { loading, error, data } = useSelector(getLicenseSeatInvitations);
  useEffect(() => {
      dispatch(fetchPendingInvitesByLicense(licenseId));
  }, []);
  return {
    loading,
    error,
    data
  };
};
